import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { InsideType } from '../../constants/enum';
import { InteractiveDimensionSystem } from '../core/geometry/Dimensions';
import { GeometryUtils } from '../../utils/geometry/GeometryUtils';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';

export default class SceneCabinetEditor {
  constructor(canvasId, onSelectMesh) {
    this.canvas = document.getElementById(canvasId);
    this.onSelectMesh = onSelectMesh;
    this.scene = new THREE.Scene();

    // Basic properties
    this.floor = null;
    this.gridHelper = null;
    this.floorSize = { width: 8, depth: 6 };
    this.loadedModules = [];
    this.selectedMesh = null;
    this.currentModule = null; // Track current loaded module (cloned module instance)

    this.planToCheck = null; // Planes to check for intersection

    this.boxes = [];
    this.wireframeBoxes = [];

    // Mouse and interaction
    this.mouse = new THREE.Vector2();
    this.raycaster = new THREE.Raycaster();

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(6, 4, 6);
    this.camera.lookAt(0, 0, 0);

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      preserveDrawingBuffer: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.setClearColor(0xf0f0f0);

    //Point door
    this.arrayPointDoor = [];
    this.arrayPointDoorPrev = [];
    this.arrayPointRecommend = [];
    this.slit = 0.0;

    this.plane = new THREE.Plane();
    this.planeIntersect = new THREE.Vector3();
    this.pIntersect = new THREE.Vector3();
    this.shift = new THREE.Vector3();
    this.normal = null;
    this.dragObject = undefined;

    //border
    this.border = [];
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(window.innerWidth, window.innerHeight);
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0px';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    this.labelRenderer.domElement.style.zIndex = '1000';
    document
      .getElementById('container__canvas-cabinet')
      ?.appendChild(this.labelRenderer.domElement);

    // Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;

    // Animation
    this._animationId = null;
    this.isDisposed = false;

    // Event listeners
    window.addEventListener('resize', this.onWindowResize.bind(this), false);

    this.dimensionSystem = new InteractiveDimensionSystem(
      this.camera,
      this.renderer.domElement,
      this.scene
    );
  }

  generateCornerPoints() {
    this.dimensionSystem.clearAll();
    this.dimensionSystem.removeFromScene(this.scene);
    this.dimensionSystem.generateCornerPoints(this.scene);
    this.dimensionSystem.addToScene(this.scene);
  }

  clearAllCornerPoints() {
    this.dimensionSystem.clearAll();
    this.dimensionSystem.removeFromScene(this.scene);
  }

  initialize(options = {}) {
    const { type = 'default' } = options;

    this.addLights();
    // this.addAxesHelper();
    this.createGround();
    this.setupEventListeners();
    this.animate();
  }

  addAxesHelper(size = 1.5) {
    const axesHelper = new THREE.AxesHelper(size);
    this.scene.add(axesHelper);
  }

  addLights() {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.scene.add(ambientLight);

    // Main directional light
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(5, 8, 3);
    dirLight.castShadow = true;

    // Shadow settings
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    dirLight.shadow.camera.left = -10;
    dirLight.shadow.camera.right = 10;
    dirLight.shadow.camera.top = 10;
    dirLight.shadow.camera.bottom = -10;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 50;

    this.scene.add(dirLight);

    // Secondary light for better illumination
    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-3, 6, -3);
    this.scene.add(dirLight2);
  }

  createGround(centerPosition = new THREE.Vector3(0, 0, 0)) {
    // Remove existing floor and grid if any
    if (this.floor) {
      this.scene.remove(this.floor);
      this.floor.geometry.dispose();
      this.floor.material.dispose();
      this.floor = null;
    }

    if (this.gridHelper) {
      this.scene.remove(this.gridHelper);
      this.gridHelper = null;
    }

    // Create floor centered at the specified position
    const geometry = new THREE.PlaneGeometry(
      this.floorSize.width,
      this.floorSize.depth
    );
    const material = new THREE.MeshStandardMaterial({
      color: 0x999999,
      transparent: false,
      // side: THREE.DoubleSide,
    });

    this.floor = new THREE.Mesh(geometry, material);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.position.set(
      centerPosition.x,
      centerPosition.y,
      centerPosition.z
    );
    this.floor.receiveShadow = true;
    this.floor.userData = { isFloor: true };

    this.scene.add(this.floor);

    // Add grid helper centered at the same position
    this.gridHelper = new THREE.GridHelper(
      Math.max(this.floorSize.width, this.floorSize.depth),
      20,
      0x444444,
      0x888888
    );
    this.gridHelper.position.set(
      centerPosition.x,
      centerPosition.y + 0.01,
      centerPosition.z
    );
    this.scene.add(this.gridHelper);
  }

  setupEventListeners() {
    this.canvas.addEventListener('click', this.onCanvasClick.bind(this));
  }

  onCanvasClick(event) {
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Check intersection with current module if it exists
    const intersectionObjects = this.currentModule?.group
      ? [this.currentModule.group]
      : this.loadedModules;
    const intersects = this.raycaster.intersectObjects(
      intersectionObjects,
      true
    );

    if (intersects.length > 0) {
      // Find the top-level module group
      let moduleObject = intersects[0].object;
      while (moduleObject.parent && moduleObject.parent.type !== 'Scene') {
        moduleObject = moduleObject.parent;
      }

      this.selectedMesh = moduleObject;

      // Call callback with module info
      if (this.onSelectMesh) {
        const position = moduleObject.position;

        this.onSelectMesh({
          id: this.currentModule?.id || 'module',
          name: this.currentModule?.name || 'Module',
          position: {
            x: position.x,
            y: position.y,
            z: position.z,
          },
          moduleData: this.currentModule,
          isCloned: this.currentModule?.isEditorClone || false,
        });
      }
    } else {
      this.selectedMesh = null;
      if (this.onSelectMesh) {
        this.onSelectMesh(null);
      }
    }
  }

  findGLTFByUuid(uuid) {
    // return this.scene.getObjectByProperty("uuid", uuid);
    return this.findInGroupByUuid(this.scene, uuid);
  }

  findInGroupByUuid(group, uuid) {
    const foundObject = group.getObjectByProperty('uuid', uuid);
    if (foundObject) {
      return foundObject;
    }
    for (let i = 0; i < group.children.length; i++) {
      const child = group.children[i];
      if (child.type === 'Group') {
        const result = this.findInGroupByUuid(child, uuid);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  async loadClonedModule(clonedModule) {
    return new Promise(async (resolve, reject) => {
      if (!clonedModule) {
        reject(new Error('No cloned module provided'));
        return;
      }

      try {
        // Clear previous module if exists
        this.clearCurrentModule();

        // Load 3D model cho cloned module nếu chưa load
        if (!clonedModule.group) {
          await clonedModule.load();
        }

        // Clone group từ module để không ảnh hưởng đến module gốc
        if (clonedModule.group) {
          // Deep clone the 3D group
          const clonedGroup = this.deepCloneGroup(
            clonedModule.group,
            clonedModule
          );

          // Hiển thị tất cả mesh bị ẩn trong clonedGroup
          clonedGroup.traverse(child => {
            if (child.isMesh) {
              child.visible = true;

              // Nếu mesh có material, đảm bảo material cũng được hiển thị
              if (child.material) {
                if (Array.isArray(child.material)) {
                  // Trường hợp material là array
                  child.material.forEach(mat => {
                    mat.visible = true;
                    mat.opacity = Math.max(mat.opacity, 0.1); // Đảm bảo opacity tối thiểu
                  });
                } else {
                  // Trường hợp material đơn lẻ
                  child.material.visible = true;
                  child.material.opacity = Math.max(
                    child.material.opacity,
                    0.1
                  );
                }
              }
            }

            // Cũng có thể áp dụng cho các object khác (Line, Points, etc.)
            child.visible = true;

            // Ẩn hoàn toàn mesh mẫu
            if (
              child.userData.type === InsideType.DOOR_TEMPLATE ||
              child.userData.type === InsideType.MESH_TEMPLATE
            ) {
              // Ẩn hoàn toàn mesh
              child.visible = false;

              // Tắt raycast để không thể click
              child.raycast = function () {};
            }
          });

          // Gán cloned group cho module
          clonedModule.group = clonedGroup;

          // Set module as current
          this.currentModule = clonedModule;
          clonedGroup.rotation.set(0, 0, 0);

          // Get module bounding box to calculate positioning
          const box = new THREE.Box3().setFromObject(
            GeometryUtils.moduleOnlyFrame(clonedGroup)
          );
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());

          // Position cloned group at origin for editing
          const modulePosition = new THREE.Vector3(size.x / 2, 0, 0);

          // Update module's position reference
          clonedModule.setPosition(modulePosition);

          clonedGroup.userData = { type: 'module' };

          // Add cloned group to scene
          this.scene.add(clonedGroup);

          // Update floor to be centered at module's base position
          const floorCenter = new THREE.Vector3(0, 0, 0);
          this.createGround(floorCenter);

          // thêm mặt phẳng check va với chuột
          // === Add vertical plane behind the module ===
          const moduleBox = new THREE.Box3().setFromObject(clonedGroup);
          const moduleSize = moduleBox.getSize(new THREE.Vector3());
          const moduleCenter = moduleBox.getCenter(new THREE.Vector3());

          // === Tạo group chứa cả 2 tấm plane ===
          const planeGroup = new THREE.Group();

          // --- Tấm phía sau lưng module ---
          const planeGeometry = new THREE.PlaneGeometry(100, 100);
          const planeMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide,
          });
          const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

          planeMesh.rotation.set(0, -Math.PI / 2, 0);
          planeMesh.position.set(
            moduleBox.min.x - 0.005,
            moduleCenter.y,
            moduleCenter.z
          );
          planeMesh.userData.type = 'planeToCheck';
          planeGroup.add(planeMesh);

          // --- Tấm phía bên trái module ---
          const leftPlaneGeometry = new THREE.PlaneGeometry(100, 100);
          const leftPlaneMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff00,
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide,
          });
          const leftPlaneMesh = new THREE.Mesh(
            leftPlaneGeometry,
            leftPlaneMaterial
          );

          leftPlaneMesh.rotation.set(0, 0, 0);
          if (clonedModule.cabinetType.toLowerCase().includes('corner')) {
            leftPlaneMesh.position.set(
              moduleCenter.x,
              moduleCenter.y,
              moduleBox.max.z - 0.005
            );
          } else {
            leftPlaneMesh.position.set(
              moduleCenter.x,
              moduleCenter.y,
              moduleBox.max.z - 5
            );
          }

          leftPlaneMesh.userData.type = 'planeToCheckLeft';
          planeGroup.add(leftPlaneMesh);

          // === Gán group vào this.planToCheck và add vào scene ===
          this.planToCheck = planeGroup;
          this.scene.add(planeGroup);

          // Update camera to view module optimally
          this.setCameraToViewModule(clonedGroup, size);

          this.handleDrawBorder(clonedGroup);

          // *** THÊM ĐOẠN NÀY ĐỂ ĐẢM BẢO COMPLETION ***
          // Sử dụng requestAnimationFrame để đảm bảo tất cả rendering hoàn thành
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Double RAF để đảm bảo chắc chắn
              resolve(clonedModule);
            });
          });
          // *** KẾT THÚC THÊM ***
        } else {
          reject(new Error('Cloned module has no 3D group'));
        }
      } catch (error) {
        console.error('Error loading cloned module:', error);
        reject(error);
      }
    });
  }

  // Thêm hàm helper để kiểm tra visibility đầy đủ
  isActuallyVisible(object) {
    let current = object;
    while (current) {
      if (!current.visible) {
        return false;
      }
      current = current.parent;
    }
    return true;
  }

  handleDrawBorder = (color = 0x000000, lineWidth = 0.5) => {
    if (!this.scene) return;
    // Xóa tất cả border hiện tại
    this.removeBorder();

    // Dùng chung 1 material cho toàn bộ wireframe của lần vẽ này
    const lineMaterial = new LineMaterial({
      color,
      linewidth: lineWidth, // pixel vật lý vì worldUnits = false
      resolution: new THREE.Vector2(
        this.renderer.domElement.width || window.innerWidth,
        this.renderer.domElement.height || window.innerHeight
      ),
      worldUnits: false,
      depthTest: true,
      depthWrite: true,
      vertexColors: false,
      dithering: false,
    });

    this.scene.traverse(child => {
      if (
        this.isActuallyVisible(child) && //mesh được hiển thị mới vẽ border
        child.isMesh &&
        child?.userData?.drawBorder !== false &&
        child.scale.x !== 0 &&
        child.scale.y !== 0 &&
        child.scale.z !== 0 &&
        child?.userData?.type !== InsideType.STOOL_CUSHION &&
        // child?.userData?.type !== InsideType.LOCK &&
        child?.userData?.type !== InsideType.HANDLE &&
        child?.userData?.type !== InsideType.ACCESSORY_GENERAL &&
        child?.userData?.type !== InsideType.ACCESSORY_DISH_UPPER &&
        child?.userData?.type !== InsideType.ACCESSORY_DISH_LOWER &&
        child?.userData?.type !== InsideType.CLOTHES_HANGER &&
        child?.userData?.type !== InsideType.ACCESSORY_COOKER &&
        child?.userData?.type !== InsideType.ACCESSORY_SINK &&
        child?.userData?.type !== InsideType.ACCESSORY_HOOD &&
        child?.userData?.type !== InsideType.ACCESSORY_FAUCET &&
        child?.userData?.type !== InsideType.ACCESSORY_FRIDGE &&
        child?.userData?.type !== InsideType.LED_CORNER &&
        child?.userData?.type !== InsideType.LED_RECESSED &&
        child?.name !== 'RAY_TRAI' &&
        child?.name !== 'RAY_PHAI' &&
        child?.name !== ''
      ) {
        //Bỏ qua những chi tiết kh cần vẽ đường viền
        if (child.userData?.requireBorder === false) {
          return;
        }

        // Vẽ wireframe theo đúng hình dạng
        const edges = new THREE.EdgesGeometry(child.geometry);
        const positions = edges.attributes.position.array;

        // Mesh không có cạnh thì bỏ qua (tránh NaN khi tính bounding sphere)
        if (!positions.length) {
          edges.dispose();
          return;
        }

        const lineGeometry = new LineSegmentsGeometry();
        lineGeometry.setPositions(positions);
        edges.dispose(); // đã copy sang lineGeometry

        const wireframe = new LineSegments2(lineGeometry, lineMaterial);

        // Copy toàn bộ transform của mesh gốc
        wireframe.position.copy(child.position);
        wireframe.rotation.copy(child.rotation);
        wireframe.scale.copy(child.scale);

        // Nếu mesh có parent transform
        if (child.parent && child.parent !== this.scene) {
          wireframe.applyMatrix4(child.parent.matrixWorld);
        }

        this.scene.add(wireframe);
        this.border.push(wireframe);
      }
    });
  };

  removeBorder() {
    this.border.forEach(box => this.scene?.remove(box));
    this.border = [];
  }

  removeWireframeBoxes() {
    this.wireframeBoxes.forEach(box => this.scene.remove(box));
    this.wireframeBoxes = [];
  }

  createWireframeBox(
    mesh,
    color = 0x569dff,
    lineThickness = 0.1,
    expandSize = 0.001
  ) {
    this.removeWireframeBoxes();

    const box = new THREE.Box3().setFromObject(mesh).expandByScalar(expandSize);

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    const vertices = [
      // Bottom face
      [-0.5, -0.5, -0.5],
      [0.5, -0.5, -0.5],
      [0.5, -0.5, -0.5],
      [0.5, -0.5, 0.5],
      [0.5, -0.5, 0.5],
      [-0.5, -0.5, 0.5],
      [-0.5, -0.5, 0.5],
      [-0.5, -0.5, -0.5],
      // Top face
      [-0.5, 0.5, -0.5],
      [0.5, 0.5, -0.5],
      [0.5, 0.5, -0.5],
      [0.5, 0.5, 0.5],
      [0.5, 0.5, 0.5],
      [-0.5, 0.5, 0.5],
      [-0.5, 0.5, 0.5],
      [-0.5, 0.5, -0.5],
      // Vertical edges
      [-0.5, -0.5, -0.5],
      [-0.5, 0.5, -0.5],
      [0.5, -0.5, -0.5],
      [0.5, 0.5, -0.5],
      [0.5, -0.5, 0.5],
      [0.5, 0.5, 0.5],
      [-0.5, -0.5, 0.5],
      [-0.5, 0.5, 0.5],
    ];
    const positionArray = new Float32Array(vertices.flat());
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positionArray, 3)
    );

    geometry.scale(size.x, size.y, size.z);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        lineColor: { value: new THREE.Color(color) },
        thickness: { value: lineThickness },
      },
      vertexShader: `
          void main() {
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
      `,
      fragmentShader: `
          uniform vec3 lineColor;
          void main() {
              gl_FragColor = vec4(lineColor, 1.0);
          }
      `,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });

    const wireframe = new THREE.LineSegments(geometry, material);
    wireframe.position.copy(center);

    this.scene.add(wireframe);
    this.wireframeBoxes.push(wireframe);
  }

  removeBoxes() {
    this.boxes.forEach(box => {
      this.scene.remove(box);
      box.geometry.dispose();
      box.material.dispose();
    });
    this.boxes = [];
  }

  createBoxBorder = result => {
    this.boxes.forEach(box => {
      this.scene.remove(box);
      box.geometry.dispose();
      box.material.dispose();
    });
    this.boxes = [];

    const rightX = result.edges.rightEdge?.mesh?.userData?.position?.x;
    const leftX = result.edges.leftEdge?.mesh?.userData?.position?.x;
    const topX = result.edges.topEdge?.mesh?.userData?.position?.x;
    const bottomX = result.edges.bottomEdge?.mesh?.userData?.position?.x;
    const leftZ = result.edges.leftEdge?.mesh?.userData?.position?.z;
    const rightZ = result.edges.rightEdge?.mesh?.userData?.position?.z;
    const topY = result.edges.topEdge?.mesh?.userData?.position?.y;
    const bottomY = result.edges.bottomEdge?.mesh?.userData?.position?.y;

    const offsetX = Math.max(
      (rightX ?? 0) + (result.sizes.sizeRight?.x ?? 0),
      (leftX ?? 0) + (result.sizes.sizeLeft?.x ?? 0),
      (topX ?? 0) + (result.sizes?.sizeTop?.x ?? 0),
      (bottomX ?? 0) + (result.sizes.sizeBottom?.x ?? 0)
    );

    const offsetZ =
      (leftZ ?? rightZ ?? 0) +
      ((rightZ ?? leftZ ?? 0) -
        (leftZ ?? rightZ ?? 0) -
        (result.sizes.sizeLeft?.z ?? 0)) /
        2;

    const offsetY =
      (bottomY ?? topY ?? 0) +
      ((topY ?? bottomY ?? 0) -
        (bottomY ?? topY ?? 0) +
        (result.sizes.sizeBottom?.y ?? 0)) /
        2;

    const width = offsetX + 0.02;
    const height =
      (topY ?? bottomY ?? 0) -
      (bottomY ?? topY ?? 0) -
      (result.sizes.sizeBottom?.y ?? 0);
    const depth =
      (leftZ ?? rightZ ?? 0) -
      (rightZ ?? leftZ ?? 0) -
      (result.sizes.sizeLeft?.z ?? 0);

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color('#B7D5FF'), // Màu sắc
      metalness: 0.5, // Chất liệu kim loại
      roughness: 0.5, // Độ nhám
      transparent: true,
      opacity: 0.5,
    });

    // Tạo Mesh từ geometry và material
    const box = new THREE.Mesh(geometry, material);

    // Đặt vị trí cho khối hộp
    box.userData.isHelperBox = true;
    box.position.set(offsetX / 2, offsetY, offsetZ);
    this.scene.add(box);
    this.boxes.push(box);
  };

  /**
   * Deep clone a THREE.Group with all its children and materials
   */
  deepCloneGroup(originalGroup, clonedModule) {
    // Tạo map UUID TRƯỚC KHI clone
    const oldUuidToNewMesh = new Map();

    // Lưu UUID cũ vào userData trước khi clone
    originalGroup.traverse(child => {
      if (child.isMesh || child.isGroup) {
        child.userData.originalUuid = child.uuid;
      }
    });

    // Clone group
    const clonedGroup = originalGroup.clone(true);

    // Xây dựng map: oldUuid -> newMesh
    clonedGroup.traverse(child => {
      if (child.isMesh || child.isGroup) {
        const oldUuid = child.userData.originalUuid;
        if (oldUuid) {
          oldUuidToNewMesh.set(oldUuid, child);
        }
      }

      // Clone materials
      if (child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map(material =>
            this.cloneMaterial(material)
          );
        } else {
          if(!child.material.color) return;
          child.material = this.cloneMaterial(child.material);
        }
      }
    });

    // Copy userData
    clonedGroup.userData = { ...originalGroup.userData };
    clonedGroup.userData.isEditorClone = true;

    // CẬP NHẬT listInside với meshes MỚI
    clonedModule?.listInside?.forEach(inside => {
      const oldUuid = inside.gltfUuid;
      const newMesh = oldUuidToNewMesh.get(oldUuid);

      if (newMesh) {
        inside.gltf = newMesh;
        inside.gltfUuid = newMesh.uuid;
      } else {
        console.warn(`⚠️ Could not find new mesh for UUID: ${oldUuid}`);
      }
    });

    return clonedGroup;
  }

  /**
   * Clone material with all its properties and textures
   */
  cloneMaterial(originalMaterial) {
    const clonedMaterial = originalMaterial.clone();

    // Clone textures to prevent shared texture issues
    Object.keys(originalMaterial).forEach(key => {
      const value = originalMaterial[key];
      if (value && value.isTexture) {
        clonedMaterial[key] = value.clone();
      }
    });

    return clonedMaterial;
  }

  // Set camera position to view module optimally
  setCameraToViewModule(moduleGroup, moduleSize) {
    if (!moduleGroup || !this.camera || !this.controls) return;

    // Get module position
    const modulePosition = moduleGroup.position.clone();

    // Calculate optimal camera distance based on module size
    const maxDimension = Math.max(moduleSize.x, moduleSize.y, moduleSize.z);
    const distance = maxDimension * 3; // 3 times the largest dimension
    const minDistance = 5; // Minimum distance
    const optimalDistance = Math.max(distance, minDistance);

    // Position camera at an angle to view module nicely
    const cameraHeight = moduleSize.y / 2 + 0.2; // Slightly above module center
    // const cameraPosition = new THREE.Vector3(
    //   modulePosition.x + optimalDistance * 0.7, // Slightly to the side
    //   cameraHeight,
    //   modulePosition.z + optimalDistance * 0.7 // Slightly forward
    // );
    const cameraPosition = new THREE.Vector3(
      modulePosition.x + optimalDistance * 0.7, // Slightly to the side
      cameraHeight,
      0
    );

    // Set camera position
    this.camera.position.copy(cameraPosition);

    // Look at module center
    const lookAtTarget = new THREE.Vector3(
      modulePosition.x,
      modulePosition.y + moduleSize.y / 2, // Look at module center height
      modulePosition.z
    );

    this.camera.lookAt(lookAtTarget);

    // Update controls target
    this.controls.target.copy(lookAtTarget);
    this.controls.update();
  }

  // Clear current module
  clearCurrentModule() {
    if (this.currentModule && this.currentModule.group) {
      // Remove cloned group from scene
      this.scene.remove(this.currentModule.group);

      // Dispose cloned resources properly
      this.disposeGroup(this.currentModule.group);

      this.currentModule = null;
    }
  }

  // Helper method to properly dispose a group and its children
  disposeGroup(group) {
    group.traverse(child => {
      // Dispose geometry
      if (child.geometry) {
        child.geometry.dispose();
      }

      // Dispose materials
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(material => {
            this.disposeMaterial(material);
          });
        } else {
          this.disposeMaterial(child.material);
        }
      }
    });
  }

  // Helper method to dispose material and its textures
  disposeMaterial(material) {
    // Dispose textures
    Object.keys(material).forEach(key => {
      const value = material[key];
      if (value && value.isTexture) {
        value.dispose();
      }
    });

    // Dispose material
    material.dispose();
  }

  // Export scene data with cloned module information
  exportSceneData() {
    const modules = [];

    // Add current cloned module if exists
    if (this.currentModule) {
      modules.push({
        id: this.currentModule.id || 'current_module',
        name: this.currentModule.name || 'Current Module',
        position: [
          this.currentModule.group.position.x,
          this.currentModule.group.position.y,
          this.currentModule.group.position.z,
        ],
        gltfUuid: this.currentModule.gltfUuid,
        isEditorClone: this.currentModule.isEditorClone,
        originalModuleId: this.currentModule.originalModuleId,
        // Export module-specific data
        moduleData: this.currentModule.getData
          ? this.currentModule.getData()
          : null,
      });
    }

    // Add other loaded modules (if any)
    this.loadedModules.forEach(module => {
      modules.push({
        id: module.userData.id,
        name: module.userData.name,
        position: [module.position.x, module.position.y, module.position.z],
        moduleData: module.userData.moduleData,
      });
    });

    return {
      floorSize: this.floorSize,
      modules: modules,
      camera: {
        position: [
          this.camera.position.x,
          this.camera.position.y,
          this.camera.position.z,
        ],
        target: [
          this.controls.target.x,
          this.controls.target.y,
          this.controls.target.z,
        ],
      },
      currentModule: this.currentModule
        ? {
            id: this.currentModule.id,
            originalModuleId: this.currentModule.originalModuleId,
            isEditorClone: true,
          }
        : null,
    };
  }

  // Update module properties (for editing features)
  updateModuleProperty(property, value) {
    if (!this.currentModule) return false;

    try {
      switch (property) {
        case 'material':
          if (this.currentModule.changeMaterial) {
            this.currentModule.changeMaterial(value);
          }
          break;

        case 'color':
          if (this.currentModule.changeColor) {
            this.currentModule.changeColor(value);
          }
          break;

        case 'doorType':
          this.currentModule.doorType = value;
          break;

        case 'handleType':
          this.currentModule.handleType = value;
          break;

        default:
          console.warn(`Unknown property: ${property}`);
          return false;
      }

      return true;
    } catch (error) {
      console.error(`Error updating ${property}:`, error);
      return false;
    }
  }

  // Get current module properties (for UI binding)
  getCurrentModuleProperties() {
    if (!this.currentModule) return null;

    return {
      id: this.currentModule.id,
      material: this.currentModule.material,
      color: this.currentModule.color,
      doorType: this.currentModule.doorType,
      handleType: this.currentModule.handleType,
      position: this.currentModule.group
        ? [
            this.currentModule.group.position.x,
            this.currentModule.group.position.y,
            this.currentModule.group.position.z,
          ]
        : [0, 0, 0],
    };
  }

  // Remove a module by ID
  removeModule(moduleId) {
    const moduleIndex = this.loadedModules.findIndex(
      module => module.userData.id === moduleId
    );

    if (moduleIndex !== -1) {
      const module = this.loadedModules[moduleIndex];
      this.scene.remove(module);

      // Dispose resources
      if (module.geometry) module.geometry.dispose();
      if (module.material) module.material.dispose();

      this.loadedModules.splice(moduleIndex, 1);

      // Clear selection if this was selected
      if (this.selectedMesh === module) {
        this.selectedMesh = null;
        if (this.onSelectMesh) {
          this.onSelectMesh(null);
        }
      }
    }
  }

  // Clear all modules
  clearModules() {
    this.loadedModules.forEach(module => {
      this.scene.remove(module);
      if (module.geometry) module.geometry.dispose();
      if (module.material) module.material.dispose();
    });
    this.loadedModules = [];
    this.selectedMesh = null;

    // Also clear current module
    this.clearCurrentModule();
  }

  // Animation loop
  animate() {
    if (this.isDisposed) return;

    this._animationId = requestAnimationFrame(() => this.animate());

    // Update controls
    if (this.controls) {
      this.controls.update();
    }

    // Render scene
    if (this.renderer && this.scene && this.camera) {
      this.labelRenderer.render(this.scene, this.camera);
      this.renderer.render(this.scene, this.camera);
    }
  }

  // Handle window resize
  onWindowResize() {
    if (this.isDisposed) return;

    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  async loadModulePreview(module) {
    return new Promise((resolve, reject) => {
      const loader = new GLTFLoader();
      loader.load(module.model, gltf => {
        const model = gltf.scene;

        model.position.set(0, 0, 0);
        model.traverse(child => {
          if (child.isMesh) {
            child.frustumCulled = false;
            child.material = child.material.clone();
            child.material.transparent = true;
            child.material.opacity = 1; // Set opacity for the preview
          }
        });
        model.userData.type = module.type;
        resolve(model);
      });
    });
  }

  // setPointDoor(door) {
  //   this.removeAllPoints();

  //   if (door) {
  //     const box = new THREE.Box3().setFromObject(door);
  //     const min = box.min;
  //     const max = box.max;

  //     const lstCorners = [
  //       new THREE.Vector3(max.x, min.y, min.z), // Góc dưới phải
  //       new THREE.Vector3(max.x, min.y, max.z), // Góc dưới trái
  //       new THREE.Vector3(max.x, max.y, max.z), // Góc trên trái
  //       new THREE.Vector3(max.x, max.y, min.z), // Góc trên phải
  //     ];

  //     const p1 = lstCorners[0];
  //     const p2 = lstCorners[1];
  //     const p3 = lstCorners[2];

  //     const vector1 = new THREE.Vector3().subVectors(p2, p1);
  //     const vector2 = new THREE.Vector3().subVectors(p3, p1);

  //     this.normal = new THREE.Vector3()
  //       .crossVectors(vector1, vector2)
  //       .normalize();

  //     const pointMaterial = new THREE.MeshBasicMaterial({ color: 0x569dff });
  //     const distance = this.camera.position.distanceTo(lstCorners[0]);

  //     lstCorners.forEach((corner, index) => {
  //       // const sphereGeometry = new THREE.SphereGeometry(0.01, 10, 10);
  //       const sphereGeometry = new THREE.SphereGeometry(
  //         0.01 + distance / 1000,
  //         10,
  //         10
  //       );
  //       const sphere = new THREE.Mesh(sphereGeometry, pointMaterial);
  //       sphere.position.copy(corner);
  //       this.scene.add(sphere);
  //       sphere.userData.index = index;
  //       sphere.userData.drawBorder = false;
  //       this.arrayPointDoor.push(sphere);

  //       //lưu để snap về vị trí cũ
  //       this.arrayPointDoorPrev.push(sphere.clone());
  //     });

  //     return lstCorners;
  //   }
  // }

  setPointDoor(door) {
    this.removeAllPoints();

    if (door) {
      const doorOrientation = GeometryUtils.getModuleOrientation(door);
      const box = new THREE.Box3().setFromObject(door);
      const min = box.min;
      const max = box.max;
      let lstCorners;

      const typeHau = door.userData?.typeHau;

      switch (doorOrientation) {
        case 'NORMAL':
          lstCorners = [
            new THREE.Vector3(max.x, min.y, min.z), // Góc dưới phải
            new THREE.Vector3(max.x, min.y, max.z), // Góc dưới trái
            new THREE.Vector3(max.x, max.y, max.z), // Góc trên trái
            new THREE.Vector3(max.x, max.y, min.z), // Góc trên phải
          ];
          break;

        case 'ROTATED_90':
          // Khi quay 270 mặt phẳng door sẽ nằm ở max.z
          lstCorners = [
            new THREE.Vector3(max.x, min.y, min.z), // Góc dưới phải
            new THREE.Vector3(min.x, min.y, min.z), // Góc dưới trái
            new THREE.Vector3(min.x, max.y, min.z), // Góc trên trái
            new THREE.Vector3(max.x, max.y, min.z), // Góc trên phải
          ];
          break;

        case 'ROTATED_180':
          lstCorners = [
            new THREE.Vector3(min.x, min.y, min.z), // Góc dưới phải
            new THREE.Vector3(min.x, min.y, max.z), // Góc dưới trái
            new THREE.Vector3(min.x, max.y, max.z), // Góc trên trái
            new THREE.Vector3(min.x, max.y, min.z), // Góc trên phải
          ];
          break;

        case 'ROTATED_270':
          // Khi quay 270 mặt phẳng door sẽ nằm ở max.z
          lstCorners = [
            new THREE.Vector3(max.x, min.y, max.z), // Góc dưới phải
            new THREE.Vector3(min.x, min.y, max.z), // Góc dưới trái
            new THREE.Vector3(min.x, max.y, max.z), // Góc trên trái
            new THREE.Vector3(max.x, max.y, max.z), // Góc trên phải
          ];
          break;
      }

      this.createDoorCorners(lstCorners);

      return lstCorners;
    }
  }

  createDoorCorners(lstCorners) {
    if (!lstCorners) return;

    const p1 = lstCorners[0];
    const p2 = lstCorners[1];
    const p3 = lstCorners[2];

    const vector1 = new THREE.Vector3().subVectors(p2, p1);
    const vector2 = new THREE.Vector3().subVectors(p3, p1);

    this.normal = new THREE.Vector3()
      .crossVectors(vector1, vector2)
      .normalize();

    const pointMaterial = new THREE.MeshBasicMaterial({ color: 0x569dff });
    const distance = this.camera.position.distanceTo(lstCorners[0]);

    lstCorners.forEach((corner, index) => {
      // const sphereGeometry = new THREE.SphereGeometry(0.01, 10, 10);
      const sphereGeometry = new THREE.SphereGeometry(
        0.01 + distance / 1000,
        10,
        10
      );
      const sphere = new THREE.Mesh(sphereGeometry, pointMaterial);
      sphere.position.copy(corner);
      this.scene.add(sphere);
      sphere.userData.index = index;
      sphere.userData.drawBorder = false;
      this.arrayPointDoor.push(sphere);

      //lưu để snap về vị trí cũ
      this.arrayPointDoorPrev.push(sphere.clone());
    });
  }

  drawPointRecommendDoorOld(result, doorType, doorCorners, door) {
    this.removePointRecommend();

    if (!result || doorCorners.length < 1) return;

    const deltaDoor = doorType === 0 ? 0 : 0.02;
    const boxL = new THREE.Box3().setFromObject(result.edges.leftEdge.mesh);
    const minL = boxL.min;
    const maxL = boxL.max;

    const boxR = new THREE.Box3().setFromObject(result.edges.rightEdge.mesh);
    const minR = boxR.min;
    const maxR = boxR.max;

    const boxT = new THREE.Box3().setFromObject(result.edges.topEdge.mesh);
    const minT = boxT.min;
    const maxT = boxT.max;

    const boxB = new THREE.Box3().setFromObject(result.edges.bottomEdge.mesh);
    const minB = boxB.min;
    const maxB = boxB.max;

    const checkL = result.edges.leftEdge.mesh.userData.type !== undefined;
    const checkR = result.edges.rightEdge.mesh.userData.type !== undefined;
    const checkT = result.edges.topEdge.mesh.userData.type !== undefined;
    const checkB = result.edges.bottomEdge.mesh.userData.type !== undefined;

    let lstPoints = [];
    if (doorType === 0) {
      // Tính trước các giá trị Z
      const rightZ = checkR ? (maxR.z + minR.z) / 2 + this.slit : minR.z;
      const leftZ = checkL ? (minL.z + maxL.z) / 2 - this.slit : maxL.z;
      const middleZ = (rightZ + leftZ) / 2;

      // Tính trước các giá trị Y
      const topY = checkT ? (maxT.y + minT.y) / 2 - this.slit : maxT.y;
      const bottomY = checkB ? (maxB.y + minB.y) / 2 + this.slit : minB.y;

      // Tính trước các giá trị Y
      const top1 = maxT.y;
      const top2 = (maxT.y + minT.y) / 2 - this.slit;
      const top3 = minT.y;

      const bottom1 = maxB.y;
      const bottom2 = (maxB.y + minB.y) / 2 - this.slit;
      const bottom3 = minB.y;

      lstPoints = [
        new THREE.Vector3(maxT.x, top1, rightZ),
        new THREE.Vector3(maxB.x, bottom1, rightZ),
        new THREE.Vector3(maxT.x, top1, leftZ),
        new THREE.Vector3(maxB.x, bottom1, leftZ),
        new THREE.Vector3(maxT.x, top1, middleZ), // Trung điểm cạnh trên
        new THREE.Vector3(maxB.x, bottom1, middleZ), // Trung điểm cạnh dưới
        new THREE.Vector3(maxT.x, top1, doorCorners[0].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom1, doorCorners[0].z), // Cùng size cửa cũ cạnh dưới trục y
        new THREE.Vector3(maxT.x, top1, doorCorners[1].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom1, doorCorners[1].z), // Cùng size cửa cũ cạnh dưới trục y

        new THREE.Vector3(maxT.x, top2, rightZ),
        new THREE.Vector3(maxT.x, top2, leftZ),
        new THREE.Vector3(maxB.x, bottom2, rightZ),
        new THREE.Vector3(maxB.x, bottom2, leftZ),
        new THREE.Vector3(maxT.x, top2, middleZ), // Trung điểm cạnh trên
        new THREE.Vector3(maxB.x, bottom2, middleZ), // Trung điểm cạnh dưới
        new THREE.Vector3(maxT.x, top2, doorCorners[0].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom2, doorCorners[0].z), // Cùng size cửa cũ cạnh dưới trục y
        new THREE.Vector3(maxT.x, top2, doorCorners[1].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom2, doorCorners[1].z), // Cùng size cửa cũ cạnh dưới trục y

        new THREE.Vector3(maxT.x, top3, rightZ),
        new THREE.Vector3(maxT.x, top3, leftZ),
        new THREE.Vector3(maxB.x, bottom3, rightZ),
        new THREE.Vector3(maxB.x, bottom3, leftZ),
        new THREE.Vector3(maxT.x, top3, middleZ), // Trung điểm cạnh trên
        new THREE.Vector3(maxB.x, bottom3, middleZ), // Trung điểm cạnh dưới
        new THREE.Vector3(maxT.x, top3, doorCorners[0].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom3, doorCorners[0].z), // Cùng size cửa cũ cạnh dưới trục y
        new THREE.Vector3(maxT.x, top3, doorCorners[1].z), // Cùng size cửa cũ cạnh trên trục y
        new THREE.Vector3(maxB.x, bottom3, doorCorners[1].z), // Cùng size cửa cũ cạnh dưới trục y
      ];
    } else {
      // lstPoints = [
      //   new THREE.Vector3(maxT.x, minT.y, minL.z),
      //   new THREE.Vector3(maxT.x, minT.y, maxR.z),
      //   new THREE.Vector3(maxB.x, maxB.y, minL.z),
      //   new THREE.Vector3(maxB.x, maxB.y, maxR.z),
      //   new THREE.Vector3(maxT.x, minT.y, (minL.z + maxR.z) / 2), // Trung điểm cạnh đáy (giữa 2 điểm đầu)
      //   new THREE.Vector3(maxB.x, maxB.y, (minL.z + maxR.z) / 2), // Trung điểm cạnh đối diện (giữa 2 điểm cuối)
      // ];
    }

    lstPoints.forEach((corner, index) => {
      const pointMaterial = new THREE.MeshBasicMaterial({ color: 0xff9668 });
      const sphereGeometry = new THREE.SphereGeometry(0.005, 10, 10);
      const sphere = new THREE.Mesh(sphereGeometry, pointMaterial);
      sphere.position.copy(corner);
      this.scene.add(sphere);
      sphere.userData.index = index;
      this.arrayPointRecommend.push(sphere);
    });

    return this.arrayPointRecommend;
  }

  drawPointRecommendDoor(result, doorType, doorCorners, door) {
    this.removePointRecommend();

    if (!result || doorCorners.length < 1) return;

    const orientation = GeometryUtils.getModuleOrientation(door);
    let lstPoints = [];

    const deltaDoor = doorType === 0 ? 0 : 0.02;

    const boxL = new THREE.Box3().setFromObject(result.edges.leftEdge.mesh);
    const minL = boxL.min;
    const maxL = boxL.max;

    const boxR = new THREE.Box3().setFromObject(result.edges.rightEdge.mesh);
    const minR = boxR.min;
    const maxR = boxR.max;

    const boxT = new THREE.Box3().setFromObject(result.edges.topEdge.mesh);
    const minT = boxT.min;
    const maxT = boxT.max;

    const boxB = new THREE.Box3().setFromObject(result.edges.bottomEdge.mesh);
    const minB = boxB.min;
    const maxB = boxB.max;

    const checkL = result.edges.leftEdge.mesh.userData.type !== undefined;
    const checkR = result.edges.rightEdge.mesh.userData.type !== undefined;
    const checkT = result.edges.topEdge.mesh.userData.type !== undefined;
    const checkB = result.edges.bottomEdge.mesh.userData.type !== undefined;

    switch (orientation) {
      case 'NORMAL':
        if (doorType === 0) {
          // Tính trước các giá trị Z
          const rightZ = checkR ? (maxR.z + minR.z) / 2 + this.slit : minR.z;
          const leftZ = checkL ? (minL.z + maxL.z) / 2 - this.slit : maxL.z;
          const middleZ = (rightZ + leftZ) / 2;

          // Tính trước các giá trị Y
          const top1 = maxT.y;
          const top2 = (maxT.y + minT.y) / 2 - this.slit;
          const top3 = minT.y;
          const top4 = doorCorners[2].y;

          const bottom1 = maxB.y;
          const bottom2 = (maxB.y + minB.y) / 2 - this.slit;
          const bottom3 = minB.y;
          const bottom4 = doorCorners[0].y;

          lstPoints = [
            new THREE.Vector3(maxT.x, top1, rightZ),
            new THREE.Vector3(maxB.x, bottom1, rightZ),
            new THREE.Vector3(maxT.x, top1, leftZ),
            new THREE.Vector3(maxB.x, bottom1, leftZ),
            new THREE.Vector3(maxT.x, top1, middleZ), // Trung điểm cạnh trên
            new THREE.Vector3(maxB.x, bottom1, middleZ), // Trung điểm cạnh dưới
            new THREE.Vector3(maxT.x, top1, doorCorners[0].z),
            new THREE.Vector3(maxB.x, bottom1, doorCorners[0].z),
            new THREE.Vector3(maxT.x, top1, doorCorners[1].z),
            new THREE.Vector3(maxB.x, bottom1, doorCorners[1].z),

            new THREE.Vector3(maxT.x, top2, rightZ),
            new THREE.Vector3(maxT.x, top2, leftZ),
            new THREE.Vector3(maxB.x, bottom2, rightZ),
            new THREE.Vector3(maxB.x, bottom2, leftZ),
            new THREE.Vector3(maxT.x, top2, middleZ),
            new THREE.Vector3(maxB.x, bottom2, middleZ),
            new THREE.Vector3(maxT.x, top2, doorCorners[0].z),
            new THREE.Vector3(maxB.x, bottom2, doorCorners[0].z),
            new THREE.Vector3(maxT.x, top2, doorCorners[1].z),
            new THREE.Vector3(maxB.x, bottom2, doorCorners[1].z),

            new THREE.Vector3(maxT.x, top3, rightZ),
            new THREE.Vector3(maxT.x, top3, leftZ),
            new THREE.Vector3(maxB.x, bottom3, rightZ),
            new THREE.Vector3(maxB.x, bottom3, leftZ),
            new THREE.Vector3(maxT.x, top3, middleZ),
            new THREE.Vector3(maxB.x, bottom3, middleZ),
            new THREE.Vector3(maxT.x, top3, doorCorners[0].z),
            new THREE.Vector3(maxB.x, bottom3, doorCorners[0].z),
            new THREE.Vector3(maxT.x, top3, doorCorners[1].z),
            new THREE.Vector3(maxB.x, bottom3, doorCorners[1].z),

            new THREE.Vector3(maxT.x, top4, rightZ),
            new THREE.Vector3(maxB.x, bottom4, rightZ),
            new THREE.Vector3(maxT.x, top4, leftZ),
            new THREE.Vector3(maxB.x, bottom4, leftZ),
            new THREE.Vector3(maxT.x, top4, middleZ),
            new THREE.Vector3(maxB.x, bottom4, middleZ),
            new THREE.Vector3(maxT.x, top4, doorCorners[0].z),
            new THREE.Vector3(maxB.x, bottom4, doorCorners[0].z),
            new THREE.Vector3(maxT.x, top4, doorCorners[1].z),
            new THREE.Vector3(maxB.x, bottom4, doorCorners[1].z),
          ];
        } else {
          // lstPoints = [
          //   new THREE.Vector3(maxT.x, minT.y, minL.z),
          //   new THREE.Vector3(maxT.x, minT.y, maxR.z),
          //   new THREE.Vector3(maxB.x, maxB.y, minL.z),
          //   new THREE.Vector3(maxB.x, maxB.y, maxR.z),
          //   new THREE.Vector3(maxT.x, minT.y, (minL.z + maxR.z) / 2),
          //   new THREE.Vector3(maxB.x, maxB.y, (minL.z + maxR.z) / 2),
          // ];
        }
        break;

      case 'ROTATED_90':
        if (doorType === 0) {
          const rightX = checkR ? (maxR.x + minR.x) / 2 + this.slit : maxR.x;
          const leftX = checkL ? (minL.x + maxL.x) / 2 - this.slit : minL.x;
          const middleX = (rightX + leftX) / 2;

          const top1 = maxT.y;
          const top2 = (maxT.y + minT.y) / 2 - this.slit;
          const top3 = minT.y;
          const top4 = doorCorners[2].y;

          const bottom1 = maxB.y;
          const bottom2 = (maxB.y + minB.y) / 2 - this.slit;
          const bottom3 = minB.y;
          const bottom4 = doorCorners[0].y;

          lstPoints = [
            new THREE.Vector3(leftX, top1, minT.z),
            new THREE.Vector3(leftX, bottom1, minB.z),
            new THREE.Vector3(middleX, top1, minT.z),
            new THREE.Vector3(middleX, bottom1, minB.z),
            new THREE.Vector3(rightX, top1, minT.z),
            new THREE.Vector3(rightX, bottom1, minB.z),
            new THREE.Vector3(doorCorners[0].x, top1, minT.z),
            new THREE.Vector3(doorCorners[0].x, bottom1, minB.z),
            new THREE.Vector3(doorCorners[1].x, top1, minT.z),
            new THREE.Vector3(doorCorners[1].x, bottom1, minB.z),

            new THREE.Vector3(leftX, top2, minT.z),
            new THREE.Vector3(leftX, bottom2, minB.z),
            new THREE.Vector3(middleX, top2, minT.z),
            new THREE.Vector3(middleX, bottom2, minB.z),
            new THREE.Vector3(rightX, top2, minT.z),
            new THREE.Vector3(rightX, bottom2, minB.z),
            new THREE.Vector3(doorCorners[0].x, top2, minT.z),
            new THREE.Vector3(doorCorners[0].x, bottom2, minB.z),
            new THREE.Vector3(doorCorners[1].x, top2, minT.z),
            new THREE.Vector3(doorCorners[1].x, bottom2, minB.z),

            new THREE.Vector3(leftX, top3, minT.z),
            new THREE.Vector3(leftX, bottom3, minB.z),
            new THREE.Vector3(middleX, top3, minT.z),
            new THREE.Vector3(middleX, bottom3, minB.z),
            new THREE.Vector3(rightX, top3, minT.z),
            new THREE.Vector3(rightX, bottom3, minB.z),
            new THREE.Vector3(doorCorners[0].x, top3, minT.z),
            new THREE.Vector3(doorCorners[0].x, bottom3, minB.z),
            new THREE.Vector3(doorCorners[1].x, top3, minT.z),
            new THREE.Vector3(doorCorners[1].x, bottom3, minB.z),

            new THREE.Vector3(leftX, top4, minT.z),
            new THREE.Vector3(leftX, bottom4, minB.z),
            new THREE.Vector3(middleX, top4, minT.z),
            new THREE.Vector3(middleX, bottom4, minB.z),
            new THREE.Vector3(rightX, top4, minT.z),
            new THREE.Vector3(rightX, bottom4, minB.z),
            new THREE.Vector3(doorCorners[0].x, top4, minT.z),
            new THREE.Vector3(doorCorners[0].x, bottom4, minB.z),
            new THREE.Vector3(doorCorners[1].x, top4, minT.z),
            new THREE.Vector3(doorCorners[1].x, bottom4, minB.z),
          ];
        } else {
          // lstPoints = [
          //   new THREE.Vector3(minL.x, minT.y, minT.z),
          //   new THREE.Vector3(maxR.x, minT.y, minT.z),
          //   new THREE.Vector3(minL.x, maxB.y, minB.z),
          //   new THREE.Vector3(maxR.x, maxB.y, minB.z),
          //   new THREE.Vector3((minL.x + maxR.x) / 2, minT.y, minT.z),
          //   new THREE.Vector3((minL.x + maxR.x) / 2, maxB.y, minB.z),
          // ];
        }
        break;

      case 'ROTATED_180':
        if (doorType === 0) {
          const rightZ = checkR ? (maxR.z + minR.z) / 2 + this.slit : minR.z;
          const leftZ = checkL ? (minL.z + maxL.z) / 2 - this.slit : maxL.z;
          const middleZ = (rightZ + leftZ) / 2;

          const top1 = maxT.y;
          const top2 = (maxT.y + minT.y) / 2 - this.slit;
          const top3 = minT.y;
          const top4 = doorCorners[2].y;

          const bottom1 = maxB.y;
          const bottom2 = (maxB.y + minB.y) / 2 - this.slit;
          const bottom3 = minB.y;
          const bottom4 = doorCorners[0].y;

          lstPoints = [
            new THREE.Vector3(minT.x, top1, rightZ),
            new THREE.Vector3(minB.x, bottom1, rightZ),
            new THREE.Vector3(minT.x, top1, leftZ),
            new THREE.Vector3(minB.x, bottom1, leftZ),
            new THREE.Vector3(minT.x, top1, middleZ),
            new THREE.Vector3(minB.x, bottom1, middleZ),
            new THREE.Vector3(minT.x, top1, doorCorners[0].z),
            new THREE.Vector3(minB.x, bottom1, doorCorners[0].z),
            new THREE.Vector3(minT.x, top1, doorCorners[1].z),
            new THREE.Vector3(minB.x, bottom1, doorCorners[1].z),

            new THREE.Vector3(minT.x, top2, rightZ),
            new THREE.Vector3(minT.x, top2, leftZ),
            new THREE.Vector3(minB.x, bottom2, rightZ),
            new THREE.Vector3(minB.x, bottom2, leftZ),
            new THREE.Vector3(minT.x, top2, middleZ),
            new THREE.Vector3(minB.x, bottom2, middleZ),
            new THREE.Vector3(minT.x, top2, doorCorners[0].z),
            new THREE.Vector3(minB.x, bottom2, doorCorners[0].z),
            new THREE.Vector3(minT.x, top2, doorCorners[1].z),
            new THREE.Vector3(minB.x, bottom2, doorCorners[1].z),

            new THREE.Vector3(minT.x, top3, rightZ),
            new THREE.Vector3(minT.x, top3, leftZ),
            new THREE.Vector3(minB.x, bottom3, rightZ),
            new THREE.Vector3(minB.x, bottom3, leftZ),
            new THREE.Vector3(minT.x, top3, middleZ),
            new THREE.Vector3(minB.x, bottom3, middleZ),
            new THREE.Vector3(minT.x, top3, doorCorners[0].z),
            new THREE.Vector3(minB.x, bottom3, doorCorners[0].z),
            new THREE.Vector3(minT.x, top3, doorCorners[1].z),
            new THREE.Vector3(minB.x, bottom3, doorCorners[1].z),

            new THREE.Vector3(minT.x, top4, rightZ),
            new THREE.Vector3(minB.x, bottom4, rightZ),
            new THREE.Vector3(minT.x, top4, leftZ),
            new THREE.Vector3(minB.x, bottom4, leftZ),
            new THREE.Vector3(minT.x, top4, middleZ),
            new THREE.Vector3(minB.x, bottom4, middleZ),
            new THREE.Vector3(minT.x, top4, doorCorners[0].z),
            new THREE.Vector3(minB.x, bottom4, doorCorners[0].z),
            new THREE.Vector3(minT.x, top4, doorCorners[1].z),
            new THREE.Vector3(minB.x, bottom4, doorCorners[1].z),
          ];
        } else {
          // lstPoints = [
          //   new THREE.Vector3(minT.x, minT.y, minL.z),
          //   new THREE.Vector3(minT.x, minT.y, maxR.z),
          //   new THREE.Vector3(minB.x, maxB.y, minL.z),
          //   new THREE.Vector3(minB.x, maxB.y, maxR.z),
          //   new THREE.Vector3(minT.x, minT.y, (minL.z + maxR.z) / 2),
          //   new THREE.Vector3(minB.x, maxB.y, (minL.z + maxR.z) / 2),
          // ];
        }
        break;

      case 'ROTATED_270':
        if (doorType === 0) {
          const rightX = checkR ? (maxR.x + minR.x) / 2 + this.slit : maxR.x;
          const leftX = checkL ? (minL.x + maxL.x) / 2 - this.slit : minL.x;
          const middleX = (rightX + leftX) / 2;

          const top1 = maxT.y;
          const top2 = (maxT.y + minT.y) / 2 - this.slit;
          const top3 = minT.y;
          const top4 = doorCorners[2].y;

          const bottom1 = maxB.y;
          const bottom2 = (maxB.y + minB.y) / 2 - this.slit;
          const bottom3 = minB.y;
          const bottom4 = doorCorners[0].y;

          lstPoints = [
            new THREE.Vector3(leftX, top1, maxT.z),
            new THREE.Vector3(leftX, bottom1, maxB.z),
            new THREE.Vector3(middleX, top1, maxT.z),
            new THREE.Vector3(middleX, bottom1, maxB.z),
            new THREE.Vector3(rightX, top1, maxT.z),
            new THREE.Vector3(rightX, bottom1, maxB.z),
            new THREE.Vector3(doorCorners[0].x, top1, maxT.z),
            new THREE.Vector3(doorCorners[0].x, bottom1, maxB.z),
            new THREE.Vector3(doorCorners[1].x, top1, maxT.z),
            new THREE.Vector3(doorCorners[1].x, bottom1, maxB.z),

            new THREE.Vector3(leftX, top2, maxT.z),
            new THREE.Vector3(leftX, bottom2, maxB.z),
            new THREE.Vector3(middleX, top2, maxT.z),
            new THREE.Vector3(middleX, bottom2, maxB.z),
            new THREE.Vector3(rightX, top2, maxT.z),
            new THREE.Vector3(rightX, bottom2, maxB.z),
            new THREE.Vector3(doorCorners[0].x, top2, maxT.z),
            new THREE.Vector3(doorCorners[0].x, bottom2, maxB.z),
            new THREE.Vector3(doorCorners[1].x, top2, maxT.z),
            new THREE.Vector3(doorCorners[1].x, bottom2, maxB.z),

            new THREE.Vector3(leftX, top3, maxT.z),
            new THREE.Vector3(leftX, bottom3, maxB.z),
            new THREE.Vector3(middleX, top3, maxT.z),
            new THREE.Vector3(middleX, bottom3, maxB.z),
            new THREE.Vector3(rightX, top3, maxT.z),
            new THREE.Vector3(rightX, bottom3, maxB.z),
            new THREE.Vector3(doorCorners[0].x, top3, maxT.z),
            new THREE.Vector3(doorCorners[0].x, bottom3, maxB.z),
            new THREE.Vector3(doorCorners[1].x, top3, maxT.z),
            new THREE.Vector3(doorCorners[1].x, bottom3, maxB.z),

            new THREE.Vector3(leftX, top4, maxT.z),
            new THREE.Vector3(leftX, bottom4, maxB.z),
            new THREE.Vector3(middleX, top4, maxT.z),
            new THREE.Vector3(middleX, bottom4, maxB.z),
            new THREE.Vector3(rightX, top4, maxT.z),
            new THREE.Vector3(rightX, bottom4, maxB.z),
            new THREE.Vector3(doorCorners[0].x, top4, maxT.z),
            new THREE.Vector3(doorCorners[0].x, bottom4, maxB.z),
            new THREE.Vector3(doorCorners[1].x, top4, maxT.z),
            new THREE.Vector3(doorCorners[1].x, bottom4, maxB.z),
          ];
        } else {
          // lstPoints = [
          //   new THREE.Vector3(minL.x, minT.y, maxT.z),
          //   new THREE.Vector3(maxR.x, minT.y, maxT.z),
          //   new THREE.Vector3(minL.x, maxB.y, maxB.z),
          //   new THREE.Vector3(maxR.x, maxB.y, maxB.z),
          //   new THREE.Vector3((minL.x + maxR.x) / 2, minT.y, maxT.z),
          //   new THREE.Vector3((minL.x + maxR.x) / 2, maxB.y, maxB.z),
          // ];
        }
        break;
    }

    lstPoints.forEach((corner, index) => {
      const pointMaterial = new THREE.MeshBasicMaterial({ color: 0xff9668 });
      const sphereGeometry = new THREE.SphereGeometry(0.005, 10, 10);
      const sphere = new THREE.Mesh(sphereGeometry, pointMaterial);
      sphere.position.copy(corner);
      this.scene.add(sphere);
      sphere.userData.index = index;
      this.arrayPointRecommend.push(sphere);
    });

    return this.arrayPointRecommend;
  }

  removePointRecommend() {
    this.arrayPointRecommend.forEach(sphere => {
      this.scene.remove(sphere);
      sphere.geometry.dispose();
      sphere.material.dispose();
    });

    this.arrayPointRecommend = [];
  }

  removeAllPoints() {
    this.arrayPointDoor.forEach(sphere => {
      this.scene.remove(sphere);
      sphere.geometry.dispose();
      sphere.material.dispose();
    });
    this.arrayPointRecommend.forEach(sphere => {
      this.scene.remove(sphere);
      sphere.geometry.dispose();
      sphere.material.dispose();
    });

    this.arrayPointDoor = [];
    this.arrayPointDoorPrev = [];
    this.arrayPointRecommend = [];
  }

  // Dispose of resources
  dispose() {
    this.dimensionSystem?.dispose();

    this.isDisposed = true;

    // Cancel animation
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
    }

    // Remove event listeners
    window.removeEventListener('resize', this.onWindowResize.bind(this));

    // Dispose controls
    if (this.controls) {
      this.controls.dispose();
    }

    // Clear modules (this will also dispose cloned module properly)
    this.clearModules();

    // Dispose floor
    if (this.floor) {
      this.scene.remove(this.floor);
      this.floor.geometry.dispose();
      this.floor.material.dispose();
    }

    // Dispose grid
    if (this.gridHelper) {
      this.scene.remove(this.gridHelper);
    }

    // Dispose renderer
    if (this.renderer) {
      this.renderer.dispose();
    }

    // Clear scene
    if (this.scene) {
      this.scene.clear();
    }
  }
}
